import { isWaiting, stageMismatch, STAGE_MISMATCH_LABELS } from "../lib/schedule";
import { headlineValue, money, splitAddress, type JobCardFaceProps } from "./cardFaceTypes";
import styles from "./CardFaceBLedger.module.css";

export const VARIANT_B_NAME = "Ledger — body + meta bar";

/**
 * PROTOTYPE ONLY.
 *
 * Premise: the card is a small document, not a list of lines. Money is the
 * number people compare between cards, so it is right-anchored on the first row
 * where it forms a readable column down the stack; everything administrative
 * (refs, item count, client) is demoted below a hairline into a meta bar, so
 * the top block can be read on its own and the bottom one ignored.
 *
 * Priority is a dot leading the title rather than a rail — it stays part of the
 * sentence "this job, at this urgency", which reads better when the board is
 * mostly one priority.
 */
export function VariantBLedger({ card }: JobCardFaceProps) {
  const { street, locality } = splitAddress(card.propertyAddress);
  const value = headlineValue(card);
  const waiting = isWaiting(card.scheduleAction ?? "");
  const mismatch = stageMismatch(card.scheduleAction ?? "", card.columnId ?? "");
  const schedule = [card.scheduleLabel, card.scheduleWhen].filter(Boolean).join(" · ");

  return (
    <div className={styles.face}>
      <div className={styles.head}>
        <strong className={styles.title} title={card.title}>
          <span className={styles.dot} data-priority={card.priority ?? "normal"} aria-hidden />
          {card.title}
        </strong>
        {value && (
          <span className={styles.value}>
            {money(value.cents)}
            <em className={styles.valueNote}>{value.estimated ? "est" : "inc GST"}</em>
          </span>
        )}
      </div>

      <span className={styles.address} title={card.propertyAddress}>
        {street}
        {locality && <span className={styles.locality}> · {locality}</span>}
      </span>

      {schedule && (
        <span className={waiting ? styles.scheduleWaiting : styles.schedule}>
          {waiting ? "⏳" : "→"} {schedule}
          {mismatch && (
            <span className={styles.flag} title={STAGE_MISMATCH_LABELS[mismatch]}>
              !
            </span>
          )}
        </span>
      )}

      <div className={styles.meta}>
        <span className={styles.chips}>
          {card.refInternal && <span className={styles.chip}>#{card.refInternal}</span>}
          {card.refExternal && <span className={styles.chip}>Ext {card.refExternal}</span>}
          {card.scopeItemCount ? (
            <span className={styles.chip}>
              {card.scopeItemCount} item{card.scopeItemCount === 1 ? "" : "s"}
            </span>
          ) : null}
        </span>
        {card.clientName && <span className={styles.client}>{card.clientName}</span>}
      </div>
    </div>
  );
}
